import { useFrame } from "@react-three/fiber";
import { useEffect, useRef } from "react";
import type { ViewerPerformanceSample } from "./viewerConfig";

const SAMPLE_WINDOW_MS = 300;
const MAX_CONTIGUOUS_FRAME_GAP_MS = 120;
const WINDOWS_PER_DECISION = 5;
const DECISION_THRESHOLD = 0.6;
const FACTOR_STEP = 0.18;
const INITIAL_FACTOR = 0.7;

function percentile(values: readonly number[], fraction: number): number {
	const ordered = [...values].sort((a, b) => a - b);
	const index = Math.min(
		ordered.length - 1,
		Math.max(0, Math.ceil(ordered.length * fraction) - 1),
	);
	return ordered[index];
}

/**
 * FPS accumulator for an R3F demand loop.
 *
 * Unlike drei's PerformanceMonitor, a pause between requested frames is not
 * render time. Gaps above MAX_CONTIGUOUS_FRAME_GAP_MS start a fresh burst and
 * are never allowed to become a near-zero FPS sample.
 */
export class DemandPerformanceTracker {
	private lastFrameAt: number | null = null;
	private frameDeltas: number[] = [];
	private windowElapsedMs = 0;
	private windowFps: number[] = [];
	private factor = INITIAL_FACTOR;
	private refreshRate = 0;

	recordFrame(now: number): ViewerPerformanceSample | null {
		if (this.lastFrameAt === null) {
			this.lastFrameAt = now;
			return null;
		}

		const delta = now - this.lastFrameAt;
		this.lastFrameAt = now;
		if (delta <= 0) return null;
		if (delta > MAX_CONTIGUOUS_FRAME_GAP_MS) {
			this.resetCurrentBurst();
			return null;
		}

		this.frameDeltas.push(delta);
		this.windowElapsedMs += delta;
		if (this.windowElapsedMs < SAMPLE_WINDOW_MS) return null;

		// A high percentile resists one lucky fast frame while the five-window
		// decision prevents one isolated hitch from changing quality.
		const representativeDelta = percentile(this.frameDeltas, 0.75);
		const fps = Math.round(1000 / representativeDelta);
		this.refreshRate = Math.max(this.refreshRate, fps);
		this.windowFps.push(fps);
		this.resetCurrentBurst();

		if (this.windowFps.length < WINDOWS_PER_DECISION) return null;

		const [lower, upper] =
			this.refreshRate > 90 ? ([52, 78] as const) : ([38, 56] as const);
		const lowerCount = this.windowFps.filter((value) => value < lower).length;
		const upperCount = this.windowFps.filter((value) => value >= upper).length;
		this.windowFps = [];

		const previousFactor = this.factor;
		if (lowerCount > WINDOWS_PER_DECISION * DECISION_THRESHOLD) {
			this.factor = Math.max(
				0,
				Math.round((this.factor - FACTOR_STEP) * 100) / 100,
			);
		} else if (upperCount > WINDOWS_PER_DECISION * DECISION_THRESHOLD) {
			this.factor = Math.min(
				1,
				Math.round((this.factor + FACTOR_STEP) * 100) / 100,
			);
		}
		if (this.factor === previousFactor) return null;

		return {
			factor: this.factor,
			fps,
			refreshrate: this.refreshRate,
			fallback: false,
		};
	}

	/** Reset timing evidence after tab visibility or renderer lifecycle gaps. */
	reset(): void {
		this.lastFrameAt = null;
		this.frameDeltas = [];
		this.windowElapsedMs = 0;
		this.windowFps = [];
		this.refreshRate = 0;
	}

	restart(): void {
		this.factor = INITIAL_FACTOR;
		this.reset();
	}

	private resetCurrentBurst(): void {
		this.frameDeltas = [];
		this.windowElapsedMs = 0;
	}
}

export function DemandPerformanceMonitor({
	onChange,
	resetKey,
}: {
	onChange?: (sample: ViewerPerformanceSample) => void;
	resetKey?: string;
}) {
	const trackerRef = useRef(new DemandPerformanceTracker());

	useEffect(() => {
		void resetKey;
		trackerRef.current.restart();
	}, [resetKey]);

	useEffect(() => {
		const reset = () => trackerRef.current.reset();
		document.addEventListener("visibilitychange", reset);
		return () => document.removeEventListener("visibilitychange", reset);
	}, []);

	useFrame(() => {
		const sample = trackerRef.current.recordFrame(performance.now());
		if (sample) onChange?.(sample);
	});

	return null;
}
